import { observer } from "mobx-react-lite"

import { SearchIcon } from "@/domains/icons"
import { TradeUpIcon } from "@/domains/icons/huge-icons/monotone/trade-up"
import { SwitchCardView } from "@/domains/common/components/switches/switch-card-view"
import { SwitchListView } from "@/domains/common/components/switches/switch-list-view"
import { ScaleCard400 } from "@/domains/design/components/scale-card-400"
import { cn } from "@/domains/library"

import type { AvailableRevenueSourcesViewProps } from "./types"
import { MinMaxFilterView } from "./min-max-filter-view"
import { RevenueSourceCardView } from "./revenue-source-card-view"
import { RevenueSourceTableView } from "./revenue-source-table-view"

export const AvailableRevenueSourcesView = observer(
  ({
    aprFilter,
    tvlFilter,
    revenueFilter,
    revenueSources,
    searchValue,
    onSearchChange,
    onUpdateAllocation,
    totalUsed,
    listView,
    onListViewChange,
    governanceToken,
    availableRevenueSourcesSortBy,
    onAvailableRevenueSourcesSortChange,
  }: AvailableRevenueSourcesViewProps) => {
    return (
      <div>
        {/* Header Section */}
        <div
          className={cn(
            "mb-6 grid w-full grid-cols-3 items-center gap-2",
            "sm:grid-cols-6",
            "lg:grid-cols-[33%_1fr_1fr_1fr_1fr]",
            "xl-gap-4 xl:grid-cols-8",
          )}
        >
          <TitleSection
            className="col-span-3 lg:col-auto xl:col-span-2"
            listView={listView}
            onListViewChange={onListViewChange}
          />
          <div className="col-span-3 lg:col-auto xl:col-span-2 xl:col-start-4">
            <SearchFilter searchValue={searchValue} onSearchChange={onSearchChange} />
          </div>
          <MinMaxFilterView
            label="APR"
            value={aprFilter.value}
            onChange={(val) => {
              const numValue = parseFloat(val.toString()) || 0
              aprFilter.setValue(numValue)
            }}
            mode={aprFilter.mode}
            onToggleMode={() => {
              aprFilter.setMode(aprFilter.mode === "min" ? "max" : "min")
            }}
            className="sm:col-span-2 lg:col-span-1"
          />
          <MinMaxFilterView
            label="TVL"
            value={tvlFilter.value}
            onChange={(val) => {
              const numValue = parseFloat(val.toString()) || 0
              tvlFilter.setValue(numValue)
            }}
            mode={tvlFilter.mode}
            onToggleMode={() => {
              tvlFilter.setMode(tvlFilter.mode === "min" ? "max" : "min")
            }}
            className="sm:col-span-2 lg:col-span-1"
          />
          <MinMaxFilterView
            label="Rev"
            value={revenueFilter.value}
            onChange={(val) => {
              const numValue = parseFloat(val.toString()) || 0
              revenueFilter.setValue(numValue)
            }}
            mode={revenueFilter.mode}
            onToggleMode={() => {
              revenueFilter.setMode(revenueFilter.mode === "min" ? "max" : "min")
            }}
            className="sm:col-span-2 lg:col-span-1"
          />
        </div>

        {/* Conditional Rendering: Table View or Card View */}
        {listView ? (
          <RevenueSourceTableView
            revenueSources={revenueSources}
            totalUsed={totalUsed}
            onUpdateAllocation={onUpdateAllocation}
            governanceToken={governanceToken}
            sortBy={availableRevenueSourcesSortBy}
            onSortChange={onAvailableRevenueSourcesSortChange}
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {revenueSources.map((revenueSource) => (
              <RevenueSourceCardView
                key={revenueSource.id}
                revenueSource={revenueSource}
                totalUsed={totalUsed}
                onUpdateAllocation={onUpdateAllocation}
              />
            ))}
          </div>
        )}
      </div>
    )
  },
)

const TitleSection = ({
  className,
  listView,
  onListViewChange,
}: {
  className?: string
  listView: boolean
  onListViewChange: (value: boolean) => void
}) => {
  return (
    <div className={cn("flex items-center justify-between gap-4", className)}>
      <div>
        <h3 className="flex items-center gap-2 text-xl font-bold text-t100">
          <TradeUpIcon size={24} className="text-green-400" strokeWidth={1.5} />
          Revenue Sources
        </h3>
        <p className="mt-1 text-sm text-t200">Discover high-yield voting opportunities</p>
      </div>
      <ScaleCard400 className="w-fit px-4 py-2">
        {!listView ? (
          <SwitchCardView
            checked={listView}
            onCheckedChange={() => onListViewChange(true)}
          />
        ) : (
          <SwitchListView
            checked={listView}
            onCheckedChange={() => onListViewChange(false)}
          />
        )}
      </ScaleCard400>
    </div>
  )
}

const SearchFilter = ({
  searchValue,
  onSearchChange,
}: {
  searchValue: string
  onSearchChange: (value: string) => void
}) => {
  return (
    <div className="flex w-full items-center gap-2 rounded-lg border border-p400-light bg-p400 p-1 sm:w-auto">
      <div className="relative flex-1 sm:w-64">
        <SearchIcon size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-t300" />
        <input
          type="text"
          placeholder="Search..."
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full border-none bg-transparent px-9 py-1.5 text-sm text-t100 outline-none placeholder:text-t300"
        />
      </div>
    </div>
  )
}
